// The partner/agency logo cluster shown next to the Sanborn vendor mark on the
// landing page and the intake form header. Driven by the public `logos` list
// (settings slice). Each logo is a stored raster served from /api/branding.
//
// Design notes:
//   - Heights are normalized and width is auto (objectFit: contain), so logos of
//     any aspect ratio sit on a common baseline without distortion. An optional
//     intrinsic width/height reserves horizontal space to avoid layout shift.
//   - Partner art is fixed-color, so in DARK mode each logo sits on a white
//     "plate" to stay legible; in light mode the plate is invisible (matches the
//     client mockup, where logos sit directly on the white header).
//   - A thin divider separates adjacent partner logos (per the client mockup).
import { Fragment } from 'react';
import { Box, Divider, Link } from '@mui/material';

export const logoSrc = (logo) => `/api/branding/logos/${logo.id}`;

const LogoPlate = ({ logo, height, maxWidth }) => {
  // Reserve horizontal space from the intrinsic ratio when we know it.
  const ratioWidth = logo.width && logo.height
    ? Math.min(maxWidth, (height * logo.width) / logo.height)
    : null;

  const image = (
    <Box
      component="img"
      src={logoSrc(logo)}
      alt={logo.alt_text || ''}
      loading="eager"
      sx={{
        display: 'block',
        height,
        width: ratioWidth ? `${Math.round(ratioWidth)}px` : 'auto',
        maxWidth,
        objectFit: 'contain',
      }}
    />
  );

  const plate = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 1,
        bgcolor: (t) => (t.palette.mode === 'dark' ? '#fff' : 'transparent'),
        p: (t) => (t.palette.mode === 'dark' ? '4px 8px' : 0),
      }}
    >
      {image}
    </Box>
  );

  if (logo.href) {
    return (
      <Link
        href={logo.href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'inline-flex', lineHeight: 0 }}
      >
        {plate}
      </Link>
    );
  }
  return plate;
};

const LogoBar = ({
  logos = [], height = 40, maxWidth = 180, gap = 2.5, sx,
}) => {
  if (!logos.length) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: gap,
        rowGap: 1,
        ...sx,
      }}
    >
      {logos.map((logo, i) => (
        <Fragment key={logo.id}>
          {i > 0 && (
            <Divider orientation="vertical" flexItem aria-hidden sx={{ my: 0.5 }} />
          )}
          <LogoPlate logo={logo} height={height} maxWidth={maxWidth} />
        </Fragment>
      ))}
    </Box>
  );
};

export default LogoBar;
